/**
 * Generates a cryptographically secure random number between 0 (inclusive) and 1 (exclusive).
 * @returns {number} A random number in the range [0, 1)
 */
const secureRandom = (): number => {
  const randomBuffer = new Uint32Array(1);
  crypto.getRandomValues(randomBuffer);
  return randomBuffer[0] / (0xffffffff + 1);
};

/**
 * Shuffles an array using the Fisher-Yates algorithm with cryptographically secure random numbers.
 * Returns a new shuffled array without modifying the original.
 *
 * @template T - The type of elements in the array
 * @param {T[]} array - The array to shuffle
 * @returns {T[]} A new shuffled array
 *
 * @example
 * const numbers = [1, 2, 3, 4, 5];
 * const shuffled = shuffleArray(numbers);
 * // shuffled might be: [3, 1, 5, 2, 4]
 */
export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(secureRandom() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
